import type { ItemOptionsQuantities, OptionGroup } from "@/types"

/**
 * Single source of truth for "would checkout accept this option-group
 * selection?". Mirrors merchant-core checkout validator ORD-CHK-1008/1009 on
 * TOTAL group quantity. Pure function for paths that build a CartItem without
 * the item modal (reorder first).
 */

const QUANTITY_TOLERANCE = 0.0005

function sumQuantities(quantities: ItemOptionsQuantities | undefined): number {
  if (!quantities) return 0
  return Object.values(quantities).reduce((total, quantity) => total + quantity, 0)
}

/** `maxPermitted <= 0` means "no ceiling", matching the option-group UI. */
function hasCeiling(optionGroup: Pick<OptionGroup, "maxPermitted">): boolean {
  return optionGroup.maxPermitted > 0
}

/**
 * True once the group holds as many options as the catalog allows, so a caller
 * distributing options across groups knows to stop feeding this one.
 */
export function isOptionGroupAtCapacity(
  optionGroup: OptionGroup,
  quantities: ItemOptionsQuantities | undefined,
): boolean {
  if (!hasCeiling(optionGroup)) return false
  return sumQuantities(quantities) >= optionGroup.maxPermitted
}

/** True while the group still needs options to reach its required minimum. */
export function isOptionGroupBelowMinimum(
  optionGroup: OptionGroup,
  quantities: ItemOptionsQuantities | undefined,
): boolean {
  return sumQuantities(quantities) < optionGroup.minPermitted
}

/**
 * True when `quantities` is a selection checkout would accept for this group:
 * it meets the required minimum and stays under the ceiling.
 *
 * Portionable groups (meio-a-meio) need no special case — the catalog already
 * publishes their effective bounds (`minPermitted == maxPermitted ==
 * numberOfFlavors`) and the shares are normalized to sum to exactly that, so
 * the same total-quantity comparison decides them.
 */
export function isOptionGroupSelectionValid(
  optionGroup: OptionGroup,
  quantities: ItemOptionsQuantities | undefined,
): boolean {
  if (optionGroup.options.length === 0) return true

  const total = sumQuantities(quantities)

  if (total < optionGroup.minPermitted - QUANTITY_TOLERANCE) return false
  if (hasCeiling(optionGroup) && total > optionGroup.maxPermitted + QUANTITY_TOLERANCE) return false
  return true
}

/**
 * Returns the first group the merchant would reject, or null when the whole
 * selection is valid.
 *
 * `optionGroups` must be EVERY group the offer currently exposes, not only the
 * ones the selection touched: a group missing from `quantitiesByGroup` is
 * validated as an empty selection on purpose, which is exactly how a mandatory
 * group added to the catalog after the fact gets caught.
 */
export function findInvalidOptionGroup(
  optionGroups: OptionGroup[],
  quantitiesByGroup: Record<string, ItemOptionsQuantities>,
): OptionGroup | null {
  return (
    optionGroups.find(
      (optionGroup) => !isOptionGroupSelectionValid(optionGroup, quantitiesByGroup[optionGroup.id]),
    ) ?? null
  )
}
